/**
 * §P.7 · El punto de la sede, en un mapa · 28-09-2026
 *
 * Antes el punto se corregía tecleando latitud y longitud, y nadie sabe las de su
 * dojang. Ahora se **arrastra el marcador**, o se pulsa en el mapa donde está la
 * entrada. Los dos campos siguen debajo, para quien las tenga copiadas.
 *
 * ## OpenStreetMap, como el geocodificador
 *
 * Mismo proveedor que sitúa las sedes (Nominatim), sin cuenta ni clave. Su política
 * de teselas pide citarlo —el «© OpenStreetMap» de la esquina, que no se quita— y
 * un uso ligero, que es lo que es: se abre al editar una sede.
 *
 * ## Leaflet se carga al abrirlo, no con el panel
 *
 * Son 150 kB que solo necesita quien corrige un punto, dos veces en la vida de un
 * club. Con `import()` Vite los parte en su propio trozo.
 */
import { useEffect, useRef } from 'react';
import type { Map as MapaLeaflet, Marker } from 'leaflet';

/**
 * Un sitio al que llevar el mapa, con el zoom que toca a su precisión. `n` cambia
 * en cada búsqueda para que buscar dos veces lo mismo vuelva a centrar.
 */
export interface Enfoque {
  readonly latitud: number;
  readonly longitud: number;
  readonly zoom: number;
  readonly n: number;
}

/** Sin punto todavía, España entera: el dueño se acerca a su pueblo. */
const ESPANA: [number, number] = [40.2, -3.7];

/** `numeric(9,6)` en la base: seis decimales son unos 10 cm. */
const redondear = (n: number) => Math.round(n * 1e6) / 1e6;

export function MapaDeSede({
  latitud,
  longitud,
  alMover,
  enfoque,
}: {
  latitud: number | null;
  longitud: number | null;
  alMover: (latitud: number, longitud: number) => void;
  /** Lo que devuelve el buscador de encima: el mapa va allí. */
  enfoque?: Enfoque | null;
}) {
  const caja = useRef<HTMLDivElement>(null);
  const mapa = useRef<MapaLeaflet | null>(null);
  const marca = useRef<Marker | null>(null);
  // Poner o mover el marcador, disponible cuando Leaflet ya ha cargado.
  const ponerMarca = useRef<((lat: number, lng: number) => void) | null>(null);
  // El último `alMover`: el mapa se monta una vez y no puede quedarse con el primero.
  const mover = useRef(alMover);
  useEffect(() => {
    mover.current = alMover;
  }, [alMover]);
  // Lo que había al montar, para centrar sin rehacer el mapa en cada tecla.
  const inicial = useRef({ latitud, longitud });

  useEffect(() => {
    let vivo = true;

    void (async () => {
      const L = (await import('leaflet')).default;
      await import('leaflet/dist/leaflet.css');
      if (!vivo || !caja.current) return;

      const { latitud: la, longitud: lo } = inicial.current;
      const conPunto = la !== null && lo !== null;
      const m = L.map(caja.current, { scrollWheelZoom: false }).setView(
        conPunto ? [la, lo] : ESPANA,
        conPunto ? 17 : 5
      );
      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution:
          '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      }).addTo(m);

      // Un icono propio y no el de Leaflet: el suyo carga imágenes por una ruta
      // que Vite no conoce, y sale un cuadro roto.
      const icono = L.divIcon({
        className: 'mapa-sede-marca',
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      });

      const poner = (lat: number, lng: number) => {
        if (marca.current) {
          marca.current.setLatLng([lat, lng]);
          return;
        }
        marca.current = L.marker([lat, lng], {
          draggable: true,
          icon: icono,
          keyboard: true,
          title: 'La entrada de la sede. Arrástralo para moverlo.',
        }).addTo(m);
        marca.current.on('dragend', () => {
          const p = marca.current!.getLatLng();
          mover.current(redondear(p.lat), redondear(p.lng));
        });
      };

      ponerMarca.current = poner;
      if (conPunto) poner(la, lo);

      m.on('click', (e) => {
        poner(e.latlng.lat, e.latlng.lng);
        mover.current(redondear(e.latlng.lat), redondear(e.latlng.lng));
      });

      mapa.current = m;
    })();

    return () => {
      vivo = false;
      mapa.current?.remove();
      mapa.current = null;
      marca.current = null;
      ponerMarca.current = null;
    };
  }, []);

  // Si se teclean las coordenadas, el marcador las sigue.
  useEffect(() => {
    if (latitud === null || longitud === null || !marca.current) return;
    const actual = marca.current.getLatLng();
    if (actual.lat !== latitud || actual.lng !== longitud) {
      marca.current.setLatLng([latitud, longitud]);
    }
  }, [latitud, longitud]);

  // Lo encontrado por el buscador: el marcador va allí y el mapa se acerca.
  useEffect(() => {
    if (!enfoque || !mapa.current || !ponerMarca.current) return;
    ponerMarca.current(enfoque.latitud, enfoque.longitud);
    mapa.current.setView([enfoque.latitud, enfoque.longitud], enfoque.zoom);
  }, [enfoque]);

  return (
    <div
      ref={caja}
      className="mapa-sede"
      role="application"
      aria-label="Mapa de la sede: pulsa donde está la entrada o arrastra el marcador"
    />
  );
}
